import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useInventoryVendorListSearchAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryVendorListSearchAPI";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaEllipsisVertical } from "react-icons/fa6";
import toast from "react-hot-toast";
import { IFinanceActionsPostBody } from "@/api_framework/api_modals/Inventory";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { useFinancePurchaseOrderListAPI } from "@/app/hooks/api_hooks/Inventory/PO/useFinancePurchaseOrderListAPI";
import { useFinanceActionsAPI } from "@/app/hooks/api_hooks/Inventory/PO/useFinanceActionsAPI";
import FinanceRejectRequestModal from "../FinanceRejectRequestModal";
import FinanceUploadDocumentsModal from "../FinanceUploadDocumentsModal";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import SapModal from "./SapModal";
import { GrFormFilter } from "react-icons/gr";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useImageConstants } from "@/app/hooks/useImageConstants";

export interface IFinancePurchaseOrderProps {
  clinic: Array<string>;
  vendor: string;
  vendorName: string;
  createdBy: string;
  status: string;
}

const FinancePurchaseOrder = () => {
  const { getRouteKey } = useRouter();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const { execute: fetchClinicList, clinicList } = useClinicTamsFilterAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinicList, "display_name", "id");
  const { execute: searchInSection } = useInventoryVendorListSearchAPI();
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 30);
  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
  const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;
  const fetchFinancePurchaseOrderList = useFinancePurchaseOrderListAPI();
  const { financePurchaseOrderList } = useRouteInfo(getRouteKey("INVENTORY", "id")).routeState.state as IInventoryState;
  const { veticDateFormat, convertToReadableFormat } = useUtils();
  const { NO_APPOINTMENTS } = useImageConstants();
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isSapModalOpen, setIsSapModalOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [selectedSapPO, setSelectedSapPO] = useState<any>(null);
  const executeFinanceAction = useFinanceActionsAPI();
  const [loader, setLoader] = useState(false);
  const [isFinanceUplooadModalOpen, setIsFinanceUploadModalOpen] = useState(false);
  // RBAC: PO_FINANCE_UPLOAD_DOCUMENT_ACTION — legacy role: central_ops
  const canUploadFinanceDoc = useFeatureAccess("PO_FINANCE_UPLOAD_DOCUMENT_ACTION");
  // RBAC: PO_FINANCE_ADMIN_ACTION — legacy role: finance_admin
  const isFinanceAdmin = useFeatureAccess("PO_FINANCE_ADMIN_ACTION");
  const [inputSearchWithPo, setInputSearchWithPo] = useState<string>("");
  const [isApproving, setIsApproving] = useState(false);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState("");
  const [_isMenuVisible, setIsMenuVisible] = useState(false);
  const [_isMenuStatusVisible, setIsMenuStatusVisible] = useState(false);
  const [filteredPOList, setFilteredPOList] = useState(financePurchaseOrderList);

  const formHook = useForm<IFinancePurchaseOrderProps>({
    mode: "onChange",
    defaultValues: {
      clinic: [mappedClinicId],
    },
  });

  useEffect(() => {
    fetchClinicList();
  }, []);

  const handleSearchPurchaseOrders = () => {
    if (inputSearchWithPo?.length < 1) return;
    fetchFinancePurchaseOrderList(undefined, false, inputSearchWithPo);
  };

  const handleEnterPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchPurchaseOrders();
    }
  };

  const onSubmit = (data: IFinancePurchaseOrderProps) => {
    const filterProps = {
      clinic_ids: data.clinic,
      vendor_ids: data.vendor ? [data.vendor] : [],
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };
    fetchFinancePurchaseOrderList(filterProps);
  };

  useEffect(() => {
    if (mappedClinicId) {
      const defaultValues = formHook.getValues();
      const filterProps = {
        clinic_ids: defaultValues.clinic,
        vendor_ids: defaultValues.vendor ? [defaultValues.vendor] : [],
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };
      fetchFinancePurchaseOrderList(filterProps);
    }
  }, [mappedClinicId, formattedStartDate, formattedEndDate]);

  const handleIconClick = (index: number) => {
    setOpenDropdownIndex((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".action-dropdown")) {
        setOpenDropdownIndex(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleActionClick = (actionKey: string, item: any) => {
    if (actionKey === "approved") {
      setSelectedSapPO(item);
      setSelectedBankAccountId("");
      setIsSapModalOpen(true);
      return;
    }
    if (actionKey === "invoice_rejected") {
      setSelectedPO(item);
      setIsRejectModalOpen(true);
      return;
    }
    if (actionKey === "upload_documents") {
      setSelectedPO(item);
      setIsFinanceUploadModalOpen(true);
      return;
    }
    const payload: IFinanceActionsPostBody = {
      po_id: item?.po_id,
      action_type: actionKey,
    };
    executeFinanceAction(payload).then((response) => {
      if (response?.status) {
        toast.success(`Action "${actionKey}" executed successfully`);
        fetchFinancePurchaseOrderList({
          clinic_ids: formHook.getValues().clinic,
          vendor_ids: formHook.getValues().vendor ? [formHook.getValues().vendor] : [],
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        });
      } else {
        toast.error(`Failed to execute action "${actionKey}"`);
      }
    });
  };

  const handleExport = async () => {
    setLoader(true);
    const defaultValues = formHook.getValues();
    const filterProps = {
      clinic_ids: defaultValues.clinic,
      vendor_ids: defaultValues.vendor ? [defaultValues.vendor] : [],
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    };
    const response = await fetchFinancePurchaseOrderList(filterProps, true);
    if (response?.status) {
      toast.success("Export successful!");
    } else {
      toast.error("Failed to export CSV");
    }
    setLoader(false);
  };

  const handleSapApprove = async (accountId: string) => {
    if (!selectedSapPO) return;
    try {
      setIsApproving(true);
      const payload: IFinanceActionsPostBody = {
        po_id: selectedSapPO.po_id,
        action_type: "approved",
        ...(accountId && accountId !== "null" ? { account_id: accountId } : {}),
      };
      const response = await executeFinanceAction(payload);
      if (response?.status) {
        toast.success("PO approved successfully");
        fetchFinancePurchaseOrderList({
          clinic_ids: formHook.getValues().clinic,
          vendor_ids: formHook.getValues().vendor ? [formHook.getValues().vendor] : [],
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        });
        setIsSapModalOpen(false);
        setSelectedSapPO(null);
        setSelectedBankAccountId("");
      } else {
        toast.error("Failed to approve PO");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setIsApproving(false);
    }
  };

  const createdByFilterList = Array.from(
    new Map(
      (financePurchaseOrderList || []).map((item) => [
        item.created_by,
        {
          id: item.created_by,
          title: item.created_by,
        },
      ]),
    ).values(),
  );

  const statusFilterList = Array.from(
    new Map(
      (financePurchaseOrderList || []).map((item) => [
        item.finance_status,
        {
          id: item.finance_status,
          title: item.finance_status,
        },
      ]),
    ).values(),
  );

  useEffect(() => {
    const createdBy = formHook.watch("createdBy");
    if (!createdBy) {
      setFilteredPOList(financePurchaseOrderList);
    } else {
      setFilteredPOList(financePurchaseOrderList.filter((item) => item.created_by === createdBy));
    }
  }, [financePurchaseOrderList, formHook.watch("createdBy")]);

  useEffect(() => {
    const status = formHook.watch("status");
    if (!status) {
      setFilteredPOList(financePurchaseOrderList);
    } else {
      setFilteredPOList(financePurchaseOrderList.filter((item) => item.finance_status === status));
    }
  }, [financePurchaseOrderList, formHook.watch("status")]);

  useEffect(() => {
    if (!isSapModalOpen) {
      setSelectedBankAccountId("");
    }
  }, [isSapModalOpen]);

  return (
    <div className="h-full">
      <div className="pb-0.5 rounded-lg bg-white px-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center  gap-2">
          <h2 className="text-sm font-semibold mt-2">Approve GRN</h2>
        </div>
        <form onSubmit={formHook.handleSubmit(onSubmit)} className="pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 items-end">
            <div className="w-full">
              <SearchableMultiSelectMenu
                withSelectAll
                label=""
                errorMessages={[{ type: "required", message: "Clinic is required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    const clincId = item.map((i) => i.id);
                    formHook.setValue("clinic", clincId);
                  }
                }}
                fieldError={formHook.formState.errors.clinic}
                register={formHook.register("clinic", { required: true })}
                selectItems={clinicsOrgItem}
                placeholder="Clinic"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={clinicsOrgItem?.filter((c) => formHook.watch("clinic")?.includes(c.id))}
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
            <div className="w-full">
              <SearchableSelectMenu
                label=""
                placeholder="Select Vendor"
                errorMessages={[{ type: "required", message: "Required" }]}
                fieldError={formHook?.formState?.errors?.vendor ?? null}
                register={formHook.register("vendor", {
                  required: false,
                })}
                onSelectItem={(item) => {
                  formHook.setValue("vendorName", item.title);
                  formHook.setValue("vendor", item.id);
                }}
                withAPISearch={{
                  apiCallback: (searchID) => searchInSection(searchID, "PO_PAGE_SEARCH"),
                  reducerIdKey: "vendor_id",
                  reducerTitleKey: "trade_name",
                }}
                defaultSelectedItemForAPISearch={{
                  id: formHook.watch("vendor"),
                  title: formHook.watch("vendorName"),
                }}
                showDropdownIcon
              />
            </div>
            <div className="w-full">
              <OMCalendarComponent
                label=""
                selectionDate={selectionDate}
                setSelectionDate={setSelectionDate}
                startDate={formattedStartDate}
                endDate={formattedEndDate}
                containerClassName="w-full"
                handelClick={() => {
                  console.log();
                }}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:col-span-2 xl:col-span-1 xl:justify-end">
              <button
                type="submit"
                className="bg-black px-4 h-9 border-transparent rounded-md text-xs text-white cursor-pointer w-full sm:w-auto"
              >
                Apply Filters
              </button>
              <button
                onClick={handleExport}
                disabled={loader}
                className={`text-white ${loader ? "bg-gray-400" : "bg-[#193A6A]"} px-4 h-9 text-xs border-transparent font-medium rounded-md w-full sm:w-auto`}
              >
                {loader ? "Loading...." : "Export as .CSV"}
              </button>
            </div>
          </div>
        </form>
        <div className="sm:flex-auto mt-3">
          <InputWithStartAndEndAddOn
            value={inputSearchWithPo}
            containerClassName="w-full sm:w-1/3"
            placeholder="Search by PO ID"
            onChange={(e) => setInputSearchWithPo(e.target.value)}
            onKeyDown={handleEnterPress}
            endAddon={
              <button onClick={handleSearchPurchaseOrders} className="font-semibold text-xs cursor-pointer">
                <MagnifyingGlassIcon className="w-4 h-4 hover:text-blue-500 hover:scale-105 transition-transform" />
              </button>
            }
            startAddon={<span className="font-semibold text-xs cursor-pointer">PO #</span>}
          />
        </div>
        <div className="mt-2 flow-root relative rounded-xl bg-[#F9FAFB]">
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[1500px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      PO Number
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      Vendor Name
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      Invoice Details
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      Credit Note
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      Clinic Name
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      GRN Date
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      <SearchableSelectMenu
                        onSelectItem={(items) => {
                          if (items) {
                            formHook.setValue("createdBy", items.id);
                            setIsMenuVisible(false);
                          }
                        }}
                        asButton
                        renderAsButton={
                          <button type="button" className="flex items-center gap-1">
                            Created By
                            <GrFormFilter className="w-4 h-4" />
                          </button>
                        }
                        register={formHook.register("createdBy")}
                        selectItems={createdByFilterList}
                        containerClassName="w-52"
                        fieldError={formHook.formState.errors.createdBy}
                        errorMessages={[]}
                      />
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider">
                      <SearchableSelectMenu
                        onSelectItem={(items) => {
                          if (items) {
                            formHook.setValue("status", items.id);
                            setIsMenuStatusVisible(false);
                          }
                        }}
                        asButton
                        renderAsButton={
                          <button type="button" className="flex items-center gap-1">
                            Status
                            <GrFormFilter className="w-4 h-4" />
                          </button>
                        }
                        register={formHook.register("status")}
                        selectItems={statusFilterList}
                        containerClassName="w-52"
                        fieldError={formHook.formState.errors.status}
                        errorMessages={[]}
                      />
                    </th>
                    <th className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider sm:sticky right-0 bg-[#F5F5F5] z-[6]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {filteredPOList.length === 0 ? (
                    <tr className="bg-white">
                      <td colSpan={9} className="rounded-2xl">
                        <div className="flex flex-col items-center justify-center h-full gap-4 py-10">
                          <img
                            src={NO_APPOINTMENTS}
                            alt="No data"
                            className="w-32 h-24 sm:w-48 sm:h-32 object-contain"
                          />
                          <p className="text-sm sm:text-base font-medium text-[#6C727F]">
                            No purchase orders found.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredPOList.map((item, index) => {
                    const isFirst = index === 0;
                    const isLast = index === filteredPOList.length - 1;
                    const isSingle = filteredPOList.length === 1;
                    return (
                      <tr
                        key={index}
                        className={`
                        bg-white
                        ${isSingle ? "rounded-xl" : ""}
                        ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                        ${isLast && !isSingle ? "rounded-b-xl" : ""}
                      `}
                      >
                        <td
                          className={`
                          px-2 py-3 whitespace-nowrap text-[#2263ff] cursor-pointer
                          ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                          ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                          ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                        `}
                          onClick={() => {
                            if (item?.po_pdf_url) {
                              window.open(item.po_pdf_url, "_blank");
                            }
                          }}
                        >
                          {item?.po_number}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-black">{item?.vendor_name}</td>
                        <td className="px-2 py-3 whitespace-nowrap">
                          <div>
                            <p
                              className="text-[#2263ff] cursor-pointer"
                              onClick={() => {
                                if (item?.invoice_pdf_url) {
                                  window.open(item.invoice_pdf_url, "_blank");
                                }
                              }}
                            >
                              {item?.invoice_number}
                            </p>
                            <p className="text-[#747984] mt-1">{veticDateFormat(item?.invoice_date)}</p>
                          </div>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap">
                          <div>
                            <p
                              className="text-[#2263ff] cursor-pointer"
                              onClick={() => {
                                if (item?.credit_note_attachment) {
                                  window.open(item?.credit_note_attachment, "_blank");
                                }
                              }}
                            >
                              {item?.credit_note_attachment ? "CN" : ""}
                            </p>
                          </div>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-black">{item?.clinic_name}</td>
                        <td
                          className="px-2 py-3 whitespace-nowrap text-[#2263ff] cursor-pointer"
                          onClick={() => {
                            if (item?.grn_pdf_url) {
                              window.open(item.grn_pdf_url, "_blank");
                            }
                          }}
                        >
                          {veticDateFormat(item?.goods_receiving_date)}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-black">
                          <div>
                            <p>{item?.created_by}</p>
                            <p className="mt-1">{veticDateFormat(item?.created_at)}</p>
                          </div>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-black">
                          <div className="flex items-center gap-1 relative">
                            <p>{convertToReadableFormat(item?.finance_status)}</p>
                            {item?.finance_status && item?.reason_of_finance_rejection && (
                              <div className="relative group">
                                <IoIosInformationCircleOutline className="cursor-pointer" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 max-w-[250px] w-max bg-black text-white text-xs p-2 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-normal break-words">
                                  {item.reason_of_finance_rejection}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          className={`
                          px-2 py-3 whitespace-nowrap relative sm:sticky right-0 bg-white sm:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)]
                          ${openDropdownIndex === index ? "z-[60]" : "z-[5]"}
                          ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                          ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                          ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                        `}
                        >
                          <FaEllipsisVertical
                            fontSize={24}
                            color="rgb(128,129,131)"
                            className={`cursor-pointer`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleIconClick(index);
                            }}
                          />
                          {openDropdownIndex === index &&
                            (() => {
                              const actions = item?.action_items as Array<Record<string, string>> | undefined;
                              if (
                                isFinanceAdmin &&
                                Array.isArray(actions) &&
                                actions.length === 1 &&
                                Object.keys(actions[0])[0] === "upload_documents"
                              ) {
                                return null;
                              }
                              return (
                                <div
                                  className={`absolute right-7 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${!actions?.length ? "" : ""
                                    }`}
                                >
                                  {actions
                                    ?.filter((actionObj) => {
                                      const key = Object.keys(actionObj)[0];
                                      return canUploadFinanceDoc
                                        ? key === "upload_documents"
                                        : key !== "upload_documents";
                                    })
                                    .map((actionObj, i) => {
                                      const key = Object.keys(actionObj)[0];
                                      const value = Object.values(actionObj)[0];
                                      return (
                                        <div
                                          key={i}
                                          className="px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 cursor-pointer"
                                          onClick={() => handleActionClick(key, item)}
                                        >
                                          {value}
                                        </div>
                                      );
                                    })}
                                </div>
                              );
                            })()}
                        </td>
                      </tr>
                    );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <SapModal
            open={isSapModalOpen}
            setOpen={setIsSapModalOpen}
            sapdata={selectedSapPO}
            onApprove={handleSapApprove}
            loading={isApproving}
            selectedBankAccountId={selectedBankAccountId}
            onSelectBankAccount={setSelectedBankAccountId}
          />
          <FinanceRejectRequestModal
            open={isRejectModalOpen}
            onSave={async (reason: string) => {
              if (selectedPO) {
                const payload: IFinanceActionsPostBody & { reason_of_rejection?: string } = {
                  po_id: selectedPO.po_id,
                  action_type: "invoice_rejected",
                  reason_of_rejection: reason,
                };
                const response = await executeFinanceAction(payload);
                if (response?.status) {
                  toast.success("Invoice rejected successfully");
                  fetchFinancePurchaseOrderList({
                    clinic_ids: formHook.getValues().clinic,
                    vendor_ids: formHook.getValues().vendor ? [formHook.getValues().vendor] : [],
                    start_date: formattedStartDate,
                    end_date: formattedEndDate,
                  });
                } else {
                  toast.error("Failed to reject invoice");
                }
                setIsRejectModalOpen(false);
                setSelectedPO(null);
              }
            }}
            setOpen={setIsRejectModalOpen}
          />
          <FinanceUploadDocumentsModal
            open={isFinanceUplooadModalOpen}
            setOpen={setIsFinanceUploadModalOpen}
            onSave={async (credit_note_image: string, invoice_image: string) => {
              if (selectedPO) {
                const payload: IFinanceActionsPostBody & {
                  credit_note_attachment?: string;
                  updated_invoice_attachment?: string;
                } = {
                  po_id: selectedPO.po_id,
                  action_type: "upload_documents",
                  credit_note_attachment: credit_note_image,
                  updated_invoice_attachment: invoice_image,
                };
                const response = await executeFinanceAction(payload);
                if (response?.status) {
                  toast.success("Invoice rejected successfully");
                  fetchFinancePurchaseOrderList({
                    clinic_ids: formHook.getValues().clinic,
                    vendor_ids: formHook.getValues().vendor ? [formHook.getValues().vendor] : [],
                    start_date: formattedStartDate,
                    end_date: formattedEndDate,
                  });
                } else {
                  toast.error("Failed to reject invoice");
                }
                setIsRejectModalOpen(false);
                setSelectedPO(null);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FinancePurchaseOrder;
